import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

export const forecastingHorizonDaysValues = [14, 30, 60, 90, 120] as const;
export type ForecastingHorizonDays =
  (typeof forecastingHorizonDaysValues)[number];

export const forecastingHorizonDaysSchema = z.union([
  z.literal(14),
  z.literal(30),
  z.literal(60),
  z.literal(90),
  z.literal(120),
]);

/** IMS merchants with forecast runs, for merchant selectors */
export const forecastingMerchantsSchema = z.array(
  z.object({ teamId: z.string(), name: z.string().nullable() }),
);

export const forecastingTimelineGroupingValues = [
  "all",
  "volume",
  "sku",
] as const;
export type ForecastingTimelineGrouping =
  (typeof forecastingTimelineGroupingValues)[number];

export const getForecastingAccuracyTimeline = {
  name: "getForecastingAccuracyTimeline" as const,
  input: z.object({
    /** Null loads only the merchant list */
    teamId: z.string().nullable(),
    horizonDays: forecastingHorizonDaysSchema,
    /**
     * "all": one series over every SKU. "volume": high- vs low-seller series
     * (Syntetos-Boylan ADI >= 1.32, classified per origin date). "sku": one
     * series for the requested sku.
     */
    grouping: z.enum(forecastingTimelineGroupingValues),
    /** Required series filter when grouping is "sku"; ignored otherwise */
    sku: z.string().nullable(),
  }),
  output: z.object({
    /** IMS merchants with forecast runs, for the merchant selector */
    merchants: forecastingMerchantsSchema,
    /**
     * Scored SKUs for the requested merchant, for the SKU selector. Empty
     * unless grouping is "sku" and a merchant is selected.
     */
    skus: z.array(z.string()),
    /**
     * WMAPE per forecast origin date for the requested merchant and horizon,
     * oldest first, one entry per charted line. Origins whose horizon window
     * hasn't fully elapsed (or with zero actuals) are omitted.
     */
    series: z.array(
      z.object({
        label: z.string(),
        points: z.array(
          z.object({ originDate: zExt.plainDate(), wmape: z.number() }),
        ),
      }),
    ),
  }),
};
